/*
 * @Author: Dong
 * @Date: 2022-05-05 17:07:43
 * @LastEditors: zhang lin
 * @LastEditTime: 2022-12-07 11:32:39
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useIntl } from 'react-intl';
import SupTable from 'sup-rc-table';
import Icon from 'root/components/Icon';
import { getSystemApplicationList, getDomainList } from 'root/services/serviceManage';
import messages from '../messages';
import PodStateList from './PodStateList';
import styles from './index.less';
import FormSearch from './FormSearch';

let timer: any;

const SystemApplication = () => {
  const intl = useIntl();
  const formSearchRef = useRef<any>();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const [tableData, setTableData] = useState<any>([]);

  const searchRef = useRef<any>({ page: 1, pageSize: 20 }); // 搜索条件
  const [searchParams, setSearchParams] = useState({
    page: 1,
    pageSize: 20,
    domain: '',
    appName: '',
    status: '',
  });

  const [columns, setColumns] = useState<any[]>([]); // 表头数据
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [curApp, setCurApp] = useState<any>({});
  const [domainList, setDomainList] = useState<any>([]); // 所属领域select

  // 状态下拉选项
  const statusList = useMemo(() => {
    return [
      {
        name: intl.formatMessage(messages.all),
        value: '',
      },
      {
        name: intl.formatMessage(messages.VIEW_RUNNING),
        value: 'VIEW_RUNNING',
      },
      {
        name: intl.formatMessage(messages.VIEW_STARTING),
        value: 'VIEW_STARTING',
      },
      {
        name: intl.formatMessage(messages.VIEW_STOPPED),
        value: 'VIEW_STOPPED',
      },
    ];
  }, []);
  // 获取所属领域列表信息
  useEffect(() => {
    getDomainList().then((res: any) => {
      const { data = [] } = res;
      data.unshift(intl.formatMessage(messages.all));
      setDomainList(data);
      setSearchParams({
        ...searchParams,
      });
      formSearchRef.current?.setFieldsValue({ domain: data[0] });
    });
  }, []);

  useEffect(() => {
    setColumns([
      {
        title: intl.formatMessage(messages.appName),
        dataIndex: 'application',
        key: 'name',
        align: 'left',
        width: 240,
      },

      {
        title: intl.formatMessage(messages.field),
        dataIndex: 'domain',
        align: 'left',
        width: 240,
        key: 'namespace',
      },
      {
        title: intl.formatMessage(messages.runningState),
        dataIndex: 'status',
        align: 'left',
        key: 'status',
        width: 240,
        render: (text: any, record: any) => {
          const { appHealthy } = record;
          return (
            <div>
              <span className={text === intl.formatMessage(messages.stopped) ? styles.stopped : ''}>{text}</span>
              {appHealthy === 2 && (
                <Icon
                  onClick={() => {
                    showModal(record);
                  }}
                  type="tip16"
                  className={styles.tipIcon}
                />
              )}
            </div>
          );
        },
      },
      {
        title: intl.formatMessage(messages.supOSRequired),
        dataIndex: 'requiredApp',
        align: 'left',
        key: 'requiredApp',
        width: 120,
        render: (text: any) => (text ? intl.formatMessage(messages.yes) : intl.formatMessage(messages.no)),
      },
      {
        title: intl.formatMessage(messages.podQuantity),
        align: 'center',
        dataIndex: 'podHealthy',
        key: 'cpuLimit',
        width: 150,
        render: (text: any, record: any) => {
          return (
            <span
              className={styles.pod}
              onClick={() => {
                showModal(record);
              }}
            >
              {`${record.podHealthy}/${record.podTotal}`}
            </span>
          );
        },
      },
    ]);
  }, []);

  useEffect(() => {
    if (searchParams.domain === intl.formatMessage(messages.all)) searchParams.domain = '';
    searchRef.current = searchParams;
    getData();
  }, [searchParams]);

  // 切换页面时清除定时器
  useEffect(() => {
    return () => {
      clearTimeout(timer);
      timer = null;
    };
  }, []);

  // 显示pod列表模态框
  const showModal = (appData: any) => {
    const { application, appHealthy, appDependencies } = appData;
    setCurApp({ application, appHealthy, appDependencies });
    setVisible(true);
  };

  // 关闭pod列表模态框
  const closeModal = () => {
    setVisible(false);
  };

  // 轮循获取列表数据
  const loop = () => {
    if (timer) {
      clearTimeout(timer);
    }

    timer = setTimeout(() => {
      getData();
    }, 10 * 1000);
  };

  // 点击查询
  const handleSubmit = (e: any) => {
    e.preventDefault();

    formSearchRef.current?.validateFields((err: any, values: any) => {
      setLoading(true);
      // setSortedInfo({});
      // setSelectedRows([]);
      setSearchParams({
        ...searchParams,
        ...values,
        page: 1,
      });
    });
  };

  // 点击重置
  const clearForm = () => {
    formSearchRef.current?.resetFields();
    setLoading(true);
    formSearchRef.current?.setFieldsValue({ domain: domainList[0] });
    setSearchParams({
      page: 1,
      pageSize: 20,
      domain: '',
      appName: '',
      status: '',
    });
  };

  // 获取列表数据
  const getData = () => {
    getSystemApplicationList({ ...searchRef.current })
      .then((res) => {
        const { apps, currentPage, pageSize, totalSize } = res.data;
        setTableData(apps);
        setPagination({
          current: currentPage,
          pageSize: pageSize,
          total: totalSize,
        });
        loop();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 翻页
  const handleSearch = (params: any, types: any) => {
    setLoading(true);
    if (types === 'pagination') {
      setSearchParams({
        ...searchParams,
        page: params?.pagination?.current,
        pageSize: params?.pagination?.pageSize,
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.query}>
        <FormSearch
          ref={formSearchRef}
          statusOptions={statusList}
          domainListOptions={domainList}
          onBtnClick={clearForm}
          onSubmit={handleSubmit}
        />
        <div className={styles.serviceAlready}>
          <span>{intl.formatMessage(messages.reFreshTime)}</span>
        </div>
      </div>

      <div className={styles.dataTable}>
        <SupTable
          controlColumns
          rowKey="application"
          loading={loading}
          pagination={pagination}
          onSearch={handleSearch}
          columns={columns}
          dataSource={tableData}
          showSearchIcon={false}
          showColumnsFilter={false}
          showSelection={false}
        />
      </div>
      <PodStateList visible={visible} closeModal={closeModal} onOk={closeModal} appConfig={curApp} />
    </div>
  );
};

export default SystemApplication;
